import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { useTitle } from "@/hooks/use-title";
import { useTranslation } from "@/i18n/LanguageContext";
import Header from "@/components/header";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Loader2 } from "lucide-react";
import { ImageUpload } from "@/components/ImageUpload";
import { useState, useEffect } from "react";

// Define User interface with cacatais property
interface User {
  id: number;
  email: string;
  cacatais: number;
  [key: string]: any; // Allow other properties
}

const formSchema = z.object({
  title: z.string().min(1, "Title is required"),
  description: z.string().min(1, "Description is required"),
  private_key: z.string().regex(/^[0-9a-fA-F]{64}$/, "Invalid hexadecimal private key"),
  prize: z.number().min(0, "Prize must be 0 or greater").default(0),
  imageUrl: z.string().optional(),
});

type FormData = z.infer<typeof formSchema>;

export default function CreatePuzzlePage() {
  const { t, formatNumber } = useTranslation();
  const { toast } = useToast();
  const [_, setLocation] = useLocation();
  const queryClient = useQueryClient();
  useTitle(t("header.create_puzzle"));
  const [feeAmount, setFeeAmount] = useState(0);
  const [totalCost, setTotalCost] = useState(0);

  const { data: user, isLoading } = useQuery<User>({
    queryKey: ["/api/auth/user"],
  });

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: "",
      description: "",
      private_key: "",
      prize: 0,
      imageUrl: "",
    },
  });

  // Watch the prize value to calculate fee
  const prizeValue = form.watch("prize");
  
  // Calculate fee and total cost when prize changes
  useEffect(() => {
    const fee = prizeValue * 0.1; // 10% fee
    setFeeAmount(fee);
    setTotalCost(prizeValue + fee);
  }, [prizeValue]);

  const createPuzzle = useMutation({
    mutationFn: async (data: FormData) => {
      // Include fee information in the request
      const requestData = {
        ...data,
        fee: feeAmount,
        totalCost: totalCost
      };
      
      const res = await fetch("/api/puzzles/custom", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestData),
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || "Failed to create puzzle");
      }

      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/puzzles"] });
      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] }); // Refresh user data to update balance
      toast({ title: "Puzzle created successfully" });
      setLocation("/puzzles");
    },
    onError: (error) => {
      toast({
        title: "Error creating puzzle",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black">
        <Header />
        <div className="container mx-auto pt-24 px-6 text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto" />
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-black">
        <Header />
        <div className="container mx-auto pt-24 px-6">
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle>{t("auth.required")}</CardTitle>
              <CardDescription>{t("auth.sign_in_required")}</CardDescription>
            </CardHeader>
          </Card>
        </div>
      </div>
    );
  }

  // if (user?.email !== "leo.majowka@gmail.com") {
  //   return (
  //     <div className="min-h-screen bg-black">
  //       <Header />
  //       <div className="container mx-auto pt-24 px-6">
  //         <Card className="bg-zinc-900 border-zinc-800">
  //           <CardHeader>
  //             <CardTitle>{t("create_puzzle.coming_soon")}</CardTitle>
  //             <CardDescription>(Soon)</CardDescription>
  //           </CardHeader>
  //         </Card>
  //       </div>
  //     </div>
  //   );
  // }

  const onSubmit = (data: FormData) => {
    // Check if user has enough balance
    if (user && totalCost > user.cacatais) {
      toast({
        title: t("puzzles.insufficientBalance"),
        description: t("create_puzzle.form.total_cost", { amount: formatNumber(totalCost, 6) }) + 
                    ` (${t("create_puzzle.form.prize_label", { amount: formatNumber(data.prize) })} + ${t("create_puzzle.form.fee_label", { amount: formatNumber(feeAmount, 6) })})`,
        variant: "destructive",
      });
      return;
    }
    
    createPuzzle.mutate(data);
  };

  return (
    <div className="min-h-screen bg-black">
      <Header />
      <div className="container mx-auto pt-24 px-6">
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <CardTitle>{t("create_puzzle.title")}</CardTitle>
            <CardDescription>{t("create_puzzle.description")}</CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <FormField
                  control={form.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("create_puzzle.form.title")}</FormLabel>
                      <FormControl>
                        <Input {...field} className="bg-zinc-800 border-zinc-700" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("create_puzzle.form.description")}</FormLabel>
                      <FormControl>
                        <Textarea 
                          {...field} 
                          className="bg-zinc-800 border-zinc-700 min-h-[100px]" 
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="imageUrl"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("create_puzzle.form.image") || "Puzzle Image (Optional)"}</FormLabel>
                      <FormControl>
                        <ImageUpload 
                          onImageUpload={(url) => field.onChange(url)} 
                          className="bg-zinc-800 border-zinc-700"
                        />
                      </FormControl>
                      <FormDescription>
                        {t("create_puzzle.form.image_description") || "Add an optional image to your puzzle"}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="private_key"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("create_puzzle.form.private_key")}</FormLabel>
                      <FormControl>
                        <Input {...field} className="bg-zinc-800 border-zinc-700 font-mono" />
                      </FormControl>
                      <FormDescription>
                        {t("create_puzzle.form.private_key_description")}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="prize"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("create_puzzle.form.prize_amount")}</FormLabel>
                      <FormControl>
                        <Input 
                          type="number"
                          step="0.000001"
                          {...field}
                          onChange={(e) => field.onChange(parseFloat(e.target.value))}
                          className="bg-zinc-800 border-zinc-700"
                        />
                      </FormControl>
                      <FormDescription>
                        {t("create_puzzle.form.prize_description")}
                        <div className="mt-2 text-amber-500">
                          <p>{t("create_puzzle.form.fee_info")}</p>
                          <p>{t("create_puzzle.form.prize_label", { amount: formatNumber(prizeValue) })}</p>
                          <p>{t("create_puzzle.form.fee_label", { amount: formatNumber(feeAmount, 2) })}</p>
                          <p>{t("create_puzzle.form.total_cost", { amount: formatNumber(totalCost, 2) })}</p>
                          {user && <p>{t("create_puzzle.form.your_balance", { amount: formatNumber(user.cacatais || 0, 2) })}</p>}
                          {user && totalCost > (user.cacatais || 0) && (
                            <p className="text-red-500 font-bold">{t("create_puzzle.form.insufficient_balance")}</p>
                          )}
                        </div>
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button 
                  type="submit" 
                  disabled={createPuzzle.isPending || (user && totalCost > (user.cacatais || 0))}
                  className="w-full"
                >
                  {createPuzzle.isPending && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  {t("create_puzzle.form.create_button")}
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}